import { computed, ref } from "vue";

import { defineStore } from "pinia";

import {
  PRODUCT_TYPE_ARLOC,
  PRODUCT_TYPE_EQUIPMENT_FINANCING,
  PRODUCT_TYPE_LINE_OF_CREDIT,
  PRODUCT_TYPE_RECEIVABLE_PURCHASE,
  PRODUCT_TYPE_TERM_LOAN,
} from "@/helpers/constants";
import { addSuffix } from "@/helpers/formatting";
import { FUNDER_STATISTICS, getTrend } from "@/helpers/statistics";
import fundersApiService from "@/services/modules/funders";

import type {
  IFunderMetric,
  IFunderStatistic,
  IStatistics,
  IStatisticsData,
} from "@/models/funders";
import type { ComputedRef, Ref } from "vue";

interface UseFunderMetricsStore {
  funderStatistics: Ref<IFunderStatistic>;
  setFunderMetrics(statistics: IFunderStatistic): void;
  setConversionRates(statistics: IFunderStatistic): void;
  resetState(): void;
  funderMetrics: ComputedRef<ReturnType<typeof generateMetrics>>;
  productMetrics: ComputedRef<
    Record<string, ReturnType<typeof generateMetrics>>
  >;
  conversionRates: ComputedRef<ReturnType<typeof generateMetrics>>;
  getStatistics(params: Record<string, string>): Promise<IFunderStatistic>;
}

const useFunderMetricsStore = defineStore(
  "funder-metrics",
  (): UseFunderMetricsStore => {
    const funderStatistics = ref<IFunderStatistic>({} as IFunderStatistic);

    const resetState = () => {
      funderStatistics.value = {} as IFunderStatistic;
    };

    function setFunderMetrics(statistics: IFunderStatistic) {
      Object.keys(FUNDER_STATISTICS.FUNDER_METRICS).forEach(
        (metric) =>
          (funderStatistics.value.general[metric] = statistics.general[metric])
      );
    }

    function setConversionRates(statistics: IFunderStatistic) {
      Object.keys(FUNDER_STATISTICS.CONVERSION_RATES).forEach(
        (rate) =>
          (funderStatistics.value.general[rate] = statistics.general[rate])
      );
    }

    const funderMetrics = computed(() => {
      return generateMetrics(
        funderStatistics.value.general,
        FUNDER_STATISTICS.FUNDER_METRICS
      );
    });

    const productMetrics = computed(() => {
      if (!funderStatistics.value.products)
        return {} as Record<string, ReturnType<typeof generateMetrics>>;
      return {
        [PRODUCT_TYPE_ARLOC]: generateMetrics(
          funderStatistics.value.products[PRODUCT_TYPE_ARLOC],
          FUNDER_STATISTICS.PRODUCT_METRICS
        ),
        [PRODUCT_TYPE_LINE_OF_CREDIT]: generateMetrics(
          funderStatistics.value.products[PRODUCT_TYPE_LINE_OF_CREDIT],
          FUNDER_STATISTICS.PRODUCT_METRICS
        ),
        [PRODUCT_TYPE_RECEIVABLE_PURCHASE]: generateMetrics(
          funderStatistics.value.products[PRODUCT_TYPE_RECEIVABLE_PURCHASE],
          FUNDER_STATISTICS.PRODUCT_METRICS
        ),
        [PRODUCT_TYPE_TERM_LOAN]: generateMetrics(
          funderStatistics.value.products[PRODUCT_TYPE_TERM_LOAN],
          FUNDER_STATISTICS.PRODUCT_METRICS
        ),
        [PRODUCT_TYPE_EQUIPMENT_FINANCING]: generateMetrics(
          funderStatistics.value.products[PRODUCT_TYPE_EQUIPMENT_FINANCING],
          FUNDER_STATISTICS.PRODUCT_METRICS
        ),
      };
    });

    const conversionRates = computed(() => {
      return generateMetrics(
        funderStatistics.value.general,
        FUNDER_STATISTICS.CONVERSION_RATES
      );
    });

    async function getStatistics(params: Record<string, string>) {
      const statistics = await fundersApiService.getFunderStatistics(params);

      switch (params.type) {
        case "metrics":
          setFunderMetrics(statistics);
          break;
        case "rates":
          setConversionRates(statistics);
          break;
        case "products":
          funderStatistics.value.products = statistics.products;
          break;
        default:
          funderStatistics.value = statistics;
      }
      return statistics;
    }

    return {
      funderStatistics,
      setFunderMetrics,
      setConversionRates,
      resetState,
      funderMetrics,
      productMetrics,
      conversionRates,
      getStatistics,
    };
  }
);

export default useFunderMetricsStore;

function generateMetrics(
  rawStats: Record<string, number | IStatisticsData>,
  anotatedStats: Record<string, IFunderMetric>
) {
  if (!rawStats) {
    return [];
  }

  return Object.keys(rawStats)
    .map((stat) => {
      const anotatedStat = anotatedStats[stat];
      if (!anotatedStat) {
        return null;
      }
      let total;
      const rawTotal = (rawStats[stat] as IStatisticsData).total;

      total = anotatedStat.formatter?.(rawTotal);

      if (anotatedStat.suffix) {
        total = addSuffix(total || rawTotal, anotatedStat.suffix);
      }
      const data = Object.values(
        (rawStats[stat] as unknown as IStatistics).data
      );

      return {
        ...anotatedStat,
        total: total || rawTotal,
        trend: getTrend(data),
        data: Object.values(
          (rawStats[stat] as unknown as { data: IStatisticsData }).data
        ),
      };
    })
    .filter((stat) => stat !== null);
}
